import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Check, Mail, Phone, RotateCcw } from 'lucide-react';
import { ContactMessage, useContactMessages } from '../../../hooks/useContactMessages';
import { pickLang } from '../../../utils/listingDisplay';
import { formatDateTime } from './superFormat';

type MessagesState = ReturnType<typeof useContactMessages>;
type Show = 'open' | 'done' | 'all';
const SHOWS: Show[] = ['open', 'done', 'all'];

const ACTION = 'inline-flex items-center gap-1 min-h-[44px] px-2 text-sm text-ink hover:underline disabled:opacity-40';

/** Contact form messages (plan 16): newest first, mark done, reply by email. */
export default function MessagesTab({ state }: { state: MessagesState }) {
  const { t } = useTranslation();
  const { messages, loading, error, setDone } = state;
  const [show, setShow] = useState<Show>('open');

  const shown = messages.filter((m) => show === 'all' || (show === 'done' ? m.done : !m.done));
  const openCount = messages.filter((m) => !m.done).length;

  if (loading) return <p className="text-sm text-ash">{t('common.loading')}</p>;
  if (error) return <p className="text-sm text-danger-600">{error}</p>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-ash">{t('contact.super.intro')}</p>
      <div className="flex rounded-[10px] border border-ink/15 p-0.5">
        {SHOWS.map((s) => (
          <button
            key={s}
            type="button"
            aria-pressed={show === s}
            onClick={() => setShow(s)}
            className={`flex-1 min-h-[44px] rounded-[10px] text-sm font-medium ${show === s ? 'bg-ink text-paper' : 'text-ink'}`}
          >
            {t(`contact.super.show.${s}`)}
            {s === 'open' ? ` (${openCount})` : ''}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <p className="text-sm text-ash">{t('contact.super.none')}</p>
      ) : (
        <ul className="space-y-2">
          {shown.map((m) => (
            <MessageRow key={m.id} message={m} onDone={(done) => setDone(m.id, done)} />
          ))}
        </ul>
      )}
    </div>
  );
}

function MessageRow({ message: m, onDone }: { message: ContactMessage; onDone: (done: boolean) => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const listingName = pickLang(m.listingNameEn, m.listingNameHe, isHebrew);
  const subject = `${t(`contact.topic.${m.topic}`)}${listingName ? ` – ${listingName}` : ''}`;

  const toggle = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await onDone(!m.done);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className={`bg-paper rounded-[16px] border p-3 space-y-2 ${m.done ? 'border-ink/10 opacity-70' : 'border-ink/30'}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-medium text-ink">{t(`contact.topic.${m.topic}`)}</p>
        <p className="text-xs text-ash">{formatDateTime(m.createdAt, isHebrew)}</p>
      </div>
      <p className="text-sm text-ink">
        <bdi>{m.name}</bdi>
        <span className="text-ash"> · {t(`contact.super.source.${m.source}`)}</span>
      </p>
      <p className="text-xs text-ash break-all">
        <bdi dir="ltr">{m.email}</bdi>
        {m.phone && (
          <>
            {' · '}
            <bdi dir="ltr">{m.phone}</bdi>
          </>
        )}
      </p>
      {m.listingId && (
        <p className="text-xs text-ash">
          {t('contact.super.listing')}:{' '}
          <Link to={`/admin?item=${m.listingId}`} className="text-ink underline">
            {listingName || m.listingId}
          </Link>
          {m.ownersEmailed > 0 && ` · ${t('contact.super.ownersEmailed', { count: m.ownersEmailed })}`}
        </p>
      )}
      <p className="text-sm text-ink whitespace-pre-wrap break-words" dir="auto">
        {m.message}
      </p>
      <div className="flex flex-wrap items-center gap-x-2 -mx-2">
        <a href={`mailto:${m.email}?subject=${encodeURIComponent(subject)}`} className={ACTION}>
          <Mail className="w-4 h-4" aria-hidden />
          {t('contact.super.reply')}
        </a>
        {m.phone && (
          <a href={`tel:${m.phone.replace(/[^\d+]/g, '')}`} className={ACTION}>
            <Phone className="w-4 h-4" aria-hidden />
            {t('directory.action.call')}
          </a>
        )}
        <button type="button" disabled={busy} onClick={toggle} className={ACTION}>
          {m.done ? <RotateCcw className="w-4 h-4" aria-hidden /> : <Check className="w-4 h-4" aria-hidden />}
          {m.done ? t('contact.super.reopen') : t('contact.super.markDone')}
        </button>
      </div>
      {failed && <p role="alert" className="text-sm text-danger-600">{t('directory.admin.saveFailed')}</p>}
    </li>
  );
}
